import {
  Autorenew,
  Devices as DevicesIcon,
  NotificationsOff,
  ReportProblem,
  SdStorage,
  SettingsPower,
  Thermostat,
  Timer,
  Wifi,
} from '@mui/icons-material';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '~/components/Tooltip';
import { useAppContext } from '~/contexts';
import type { DeviceKind } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { formatDeviceKind } from '~/lib/string';
import { formatUptime } from '~/pages/devices/show/lib/utils';
import { useOperationalData, type StatusFilter } from '../lib/useOperationalData';
import {
  AnnouncementAlerts,
  DeviceAlertCard,
  DonutChart,
  HealthByGroupChart,
  LatestAnnouncements,
  LatestAuditLogs,
  LicenseCard,
  LoadingCard,
  QuickLinks,
  StatCard,
  type ThresholdOption,
} from './';

// NOTE: These threshold values must match the arrays in app/queries/device_filter_query.rb
// Storage threshold options (percentage available)
const STORAGE_THRESHOLD_OPTIONS: ThresholdOption[] = [
  { value: 10, label: '10%' },
  { value: 25, label: '25%' },
  { value: 50, label: '50%' },
];

// Temperature threshold options (Celsius with Fahrenheit display)
const TEMPERATURE_THRESHOLD_OPTIONS: ThresholdOption[] = [
  { value: 66, label: '150°F (66°C)' },
  { value: 71, label: '160°F (71°C)' },
  { value: 77, label: '170°F (77°C)' },
];

// Mapping from Celsius threshold values to display Fahrenheit values
const CELSIUS_TO_FAHRENHEIT: Partial<Record<number, number>> = {
  66: 150,
  71: 160,
  77: 170,
};
const toFahrenheit = (celsius: number) =>
  CELSIUS_TO_FAHRENHEIT[celsius] ?? Math.round((celsius * 9) / 5 + 32);

// Restart time window options (hours)
const RESTART_HOURS_OPTIONS: ThresholdOption[] = [
  { value: 1, label: '1h' },
  { value: 4, label: '4h' },
  { value: 12, label: '12h' },
  { value: 24, label: '24h' },
];

const statusLabel = (filter: StatusFilter) => {
  switch (filter) {
    case 'online':
      return 'online ';
    case 'offline':
      return 'offline ';
    case 'all':
      return '';
  }
};

const statusParam = (filter: StatusFilter) => {
  switch (filter) {
    case 'online':
      return '&status=["HEALTHY"]';
    case 'offline':
      return '&status=["WARN","ERROR"]';
    case 'all':
      return '&status=["HEALTHY","WARN","ERROR"]';
  }
};

export const OperationalView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const link = useLink();

  const {
    totalDevices,
    onlineDevices,
    offlineDevices,
    warningDevices,
    silencedDevices,
    displayOnCount,
    displayOffCount,
    shouldShowLicenses,
    deviceLicenses,
    deviceLicensesUsed,
    deviceCountsByKind,
    groupHealth,
    deviceHealthLoading,
    lowStorageDevices,
    lowStorageLoading,
    lowStorageCount,
    highTempDevices,
    highTempLoading,
    highTempCount,
    isSysadmin,
    uptimeData,
    uptimeLoading,
    recentRestartCount,
    recentActivityData,
    recentActivityLoading,
    storageThreshold,
    setStorageThreshold,
    temperatureThreshold,
    setTemperatureThreshold,
    restartHours,
    setRestartHours,
    storageStatusSelection,
    setStorageStatusSelection,
    tempStatusSelection,
    setTempStatusSelection,
    uptimeStatusSelection,
    setUptimeStatusSelection,
    canManage,
  } = useOperationalData();

  const { announcementCount } = useAppContext();
  // Sysadmins are scoped to every announcement ever published, so neither surface is personal.
  const showAnnouncements = !isSysadmin && announcementCount > 0;
  const recentActivityWidth = isSysadmin ? 4 : showAnnouncements ? 6 : 12;

  // Devices by kind chart data
  const devicesByKind = useMemo(() => {
    return deviceCountsByKind.map(({ filter, count }) => {
      const kind = filter.toUpperCase() as DeviceKind;
      return {
        id: filter,
        label: formatDeviceKind(kind),
        value: count,
        color: theme.palette.deviceKinds[kind.toLowerCase()],
        onClick: () => navigate(link(`/devices?kind=["${kind}"]`)),
      };
    });
  }, [deviceCountsByKind, navigate, link, theme.palette.deviceKinds]);

  // Recent restart devices - show only the relevant uptime with an icon indicating type
  // Reboots show os_uptime (hardware), Restarts show process_uptime (software)
  // Commanded restarts are informational (muted), unexpected ones are problems (inherit alert color)
  const uptimeAlertDevices = useMemo(() => {
    const rebootTitle = (commanded: boolean) =>
      commanded
        ? t('pages.dashboard.device_alerts.hardware_reboot_via_cms')
        : t('pages.dashboard.device_alerts.hardware_reboot');
    const restartTitle = (commanded: boolean) =>
      commanded
        ? t('pages.dashboard.device_alerts.software_restart_via_cms')
        : t('pages.dashboard.device_alerts.software_restart');
    // Text as well as the muted color marks a command sent from the CMS (WCAG 1.4.1).
    const viaCms = (
      <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
        {t('pages.dashboard.device_alerts.sent_via_cms')}
      </Typography>
    );

    const reboots = (uptimeData?.network?.recentReboots.nodes ?? []).map((device) => ({
      ...device,
      sortUptime: Number(device.osUptime),
      value: (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: device.wasCommanded ? 'text.secondary' : 'inherit',
          }}
        >
          {formatUptime(Number(device.osUptime))}
          <Tooltip describeChild={false} title={rebootTitle(device.wasCommanded)}>
            <SettingsPower
              sx={{ fontSize: 18, ml: 0.5 }}
              color="inherit"
              titleAccess={rebootTitle(device.wasCommanded)}
            />
          </Tooltip>
          {device.wasCommanded && viaCms}
        </Box>
      ),
    }));

    const restarts = (uptimeData?.network?.recentRestarts.nodes ?? []).map((device) => ({
      ...device,
      sortUptime: Number(device.processUptime),
      value: (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: device.wasCommanded ? 'text.secondary' : 'inherit',
          }}
        >
          {formatUptime(Number(device.processUptime))}
          <Tooltip describeChild={false} title={restartTitle(device.wasCommanded)}>
            <Autorenew
              sx={{ fontSize: 18, ml: 0.5 }}
              color="inherit"
              titleAccess={restartTitle(device.wasCommanded)}
            />
          </Tooltip>
          {device.wasCommanded && viaCms}
        </Box>
      ),
    }));
    // Combine and sort by the relevant uptime (lowest first = most recent)
    return [...reboots, ...restarts].sort((a, b) => a.sortUptime - b.sortUptime).slice(0, 5);
  }, [uptimeData?.network?.recentReboots.nodes, uptimeData?.network?.recentRestarts.nodes, t]);

  // Device status donut data
  const deviceStatusData = useMemo(
    () => [
      {
        id: 'online',
        label: t('pages.dashboard.charts.online'),
        value: onlineDevices,
        color: theme.palette.success.main,
        onClick: () => navigate(link('/devices?status=["HEALTHY"]')),
      },
      {
        id: 'offline',
        label: t('pages.dashboard.charts.offline'),
        value: offlineDevices,
        color: theme.palette.error.main,
        onClick: () => navigate(link('/devices?status=["ERROR"]')),
      },
    ],
    [onlineDevices, offlineDevices, t, theme, navigate, link],
  );

  // Display status donut data
  const displayStatusData = useMemo(
    () => [
      {
        id: 'on',
        label: t('pages.dashboard.charts.display_on'),
        value: displayOnCount,
        color: theme.palette.success.main,
        onClick: () => navigate(link('/devices?displayOn=[true]')),
      },
      {
        id: 'off',
        label: t('pages.dashboard.charts.display_off'),
        value: displayOffCount,
        color: theme.palette.error.main,
        onClick: () => navigate(link('/devices?displayOn=[false]')),
      },
    ],
    [displayOnCount, displayOffCount, t, theme, navigate, link],
  );

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      {canManage && (
        <>
          {!isSysadmin && <AnnouncementAlerts />}
          {/* Stat Cards Row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={shouldShowLicenses ? 2.4 : 3}>
              <StatCard
                icon={<DevicesIcon />}
                value={totalDevices}
                label={t('pages.dashboard.stats.total_devices')}
                color={theme.palette.primary.main}
                to={link('/devices')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={shouldShowLicenses ? 2.4 : 3}>
              <StatCard
                icon={<Wifi />}
                value={onlineDevices}
                label={t('pages.dashboard.stats.online_now')}
                color={theme.palette.success.main}
                to={link('/devices?status=["HEALTHY"]')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={shouldShowLicenses ? 2.4 : 3}>
              <StatCard
                icon={<ReportProblem />}
                value={offlineDevices + warningDevices}
                label={t('pages.dashboard.stats.issues')}
                color={theme.palette.error.main}
                to={link('/devices?status=["ERROR","WARN"]')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={shouldShowLicenses ? 2.4 : 3}>
              <StatCard
                icon={<NotificationsOff />}
                value={silencedDevices}
                label={t('pages.dashboard.stats.silenced')}
                color={theme.palette.text.secondary}
                to={link('/devices?silence=[true]')}
              />
            </Grid>
            {shouldShowLicenses && (
              <Grid item xs={12} sm={6} md={2.4}>
                <LicenseCard used={deviceLicensesUsed} total={deviceLicenses} />
              </Grid>
            )}
          </Grid>

          {/* Donut Charts Row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={isSysadmin ? 4 : 6}>
              <DonutChart
                title={t('pages.dashboard.charts.device_status')}
                data={deviceStatusData}
                centerValue={onlineDevices}
                centerLabel={t('pages.dashboard.charts.online')}
                emptyMessage={
                  totalDevices === 0 ? t('pages.dashboard.charts.no_devices') : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={isSysadmin ? 4 : 6}>
              <DonutChart
                title={t('pages.dashboard.charts.display_status')}
                data={displayStatusData}
                centerValue={displayOnCount}
                centerLabel={t('pages.dashboard.charts.display_on')}
                emptyMessage={
                  totalDevices === 0 ? t('pages.dashboard.charts.no_devices') : undefined
                }
              />
            </Grid>
            {isSysadmin && (
              <Grid item xs={12} sm={6} md={4}>
                {deviceHealthLoading ? (
                  <LoadingCard height={200} />
                ) : (
                  <DonutChart
                    title={t('pages.dashboard.charts.devices_by_type')}
                    data={devicesByKind}
                    emptyMessage={
                      totalDevices === 0 ? t('pages.dashboard.charts.no_devices') : undefined
                    }
                  />
                )}
              </Grid>
            )}
          </Grid>

          {/* Device Alerts Row - sysadmin only */}
          {isSysadmin && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <DeviceAlertCard
                  icon={<SdStorage fontSize="small" />}
                  title={t('pages.dashboard.device_alerts.low_storage')}
                  subtitle={t('pages.dashboard.device_alerts.low_storage_subtitle', {
                    count: lowStorageCount,
                    total: totalDevices,
                    threshold: storageThreshold,
                  })}
                  count={lowStorageCount}
                  totalDevices={totalDevices}
                  color={theme.palette.warning.main}
                  devices={lowStorageDevices}
                  viewAllLink={link(
                    `/devices?storageBelow=${storageThreshold}${statusParam(
                      storageStatusSelection,
                    )}`,
                  )}
                  emptyMessage={t('pages.dashboard.device_alerts.no_low_storage', {
                    threshold: storageThreshold,
                    status: statusLabel(storageStatusSelection),
                  })}
                  loading={lowStorageLoading}
                  thresholdOptions={STORAGE_THRESHOLD_OPTIONS}
                  selectedThreshold={storageThreshold}
                  onThresholdChange={setStorageThreshold}
                  thresholdLabel={t('pages.dashboard.device_alerts.below')}
                  thresholdSuffix={t('pages.dashboard.device_alerts.storage')}
                  statusFilter={storageStatusSelection}
                  onStatusFilterChange={setStorageStatusSelection}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DeviceAlertCard
                  icon={<Thermostat fontSize="small" />}
                  title={t('pages.dashboard.device_alerts.high_temperature')}
                  subtitle={t('pages.dashboard.device_alerts.high_temperature_subtitle', {
                    count: highTempCount,
                    total: totalDevices,
                    fahrenheit: toFahrenheit(temperatureThreshold),
                    celsius: temperatureThreshold,
                  })}
                  count={highTempCount}
                  totalDevices={totalDevices}
                  color={theme.palette.error.main}
                  devices={highTempDevices}
                  viewAllLink={link(
                    `/devices?temperatureAbove=${temperatureThreshold}${statusParam(
                      tempStatusSelection,
                    )}`,
                  )}
                  emptyMessage={t('pages.dashboard.device_alerts.no_high_temperature', {
                    fahrenheit: toFahrenheit(temperatureThreshold),
                    celsius: temperatureThreshold,
                    status: statusLabel(tempStatusSelection),
                  })}
                  loading={highTempLoading}
                  thresholdOptions={TEMPERATURE_THRESHOLD_OPTIONS}
                  selectedThreshold={temperatureThreshold}
                  onThresholdChange={setTemperatureThreshold}
                  thresholdLabel={t('pages.dashboard.device_alerts.above')}
                  statusFilter={tempStatusSelection}
                  onStatusFilterChange={setTempStatusSelection}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DeviceAlertCard
                  icon={<Timer fontSize="small" />}
                  title={t('pages.dashboard.device_alerts.uptime')}
                  subtitle={t('pages.dashboard.device_alerts.uptime_subtitle', {
                    count: recentRestartCount,
                    total: onlineDevices,
                    hours: restartHours,
                  })}
                  count={recentRestartCount}
                  totalDevices={onlineDevices}
                  color={theme.palette.error.main}
                  devices={uptimeAlertDevices}
                  viewAllLink={link(
                    `/devices?uptimeBelow=${restartHours}${statusParam(uptimeStatusSelection)}`,
                  )}
                  emptyMessage={t('pages.dashboard.device_alerts.no_uptime_alerts', {
                    hours: restartHours,
                    status: statusLabel(uptimeStatusSelection),
                  })}
                  loading={uptimeLoading}
                  thresholdOptions={RESTART_HOURS_OPTIONS}
                  selectedThreshold={restartHours}
                  onThresholdChange={setRestartHours}
                  thresholdLabel={t('pages.dashboard.device_alerts.within')}
                  statusFilter={uptimeStatusSelection}
                  onStatusFilterChange={setUptimeStatusSelection}
                />
              </Grid>
            </Grid>
          )}

          {/* Health by Group and Audit Logs Row */}
          <Grid container spacing={2}>
            {isSysadmin && (
              <Grid item xs={12} md={8}>
                {deviceHealthLoading ? (
                  <LoadingCard height={300} />
                ) : (
                  <HealthByGroupChart groups={groupHealth} />
                )}
              </Grid>
            )}
            <Grid item xs={12} md={recentActivityWidth}>
              {recentActivityLoading ? (
                <LoadingCard height={300} />
              ) : (
                <LatestAuditLogs auditLogs={recentActivityData?.network?.auditLogs.nodes ?? []} />
              )}
            </Grid>
            {/* Gated here, not inside the card, or an empty grid item leaves a gap. */}
            {showAnnouncements && (
              <Grid item xs={12} md={6}>
                <LatestAnnouncements />
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Quick Links - shown for non-admin users (not sysadmin/network owner) */}
      {!canManage && <QuickLinks />}
    </Box>
  );
};
